import { useEffect, useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { DatePicker, Form, Input, InputNumber, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import AuthService from 'core/auth/auth.service';
import LinksButton from 'shared/design-system/components/links-button';
import { filterOption } from 'shared/utils/common.utils';
import { getClFilters } from 'pages/private/management-tools/_redux/actions';
import { filtriClSelector } from 'pages/private/management-tools/_redux/selectors';
import { RptOpportunitySearchDto } from '@api/client';
import { getAgendaFilterOptions } from '../../../agenda-actions/_redux/actions';
import { agendaFilterOptionsSelector } from '../../../agenda-actions/_redux/selectors';

interface Props {
  onSearch: (filters: Partial<RptOpportunitySearchDto>) => void;
  onReset: () => void;
  initialValues?: Pick<RptOpportunitySearchDto, 'stati' | 'lastModified'>;
}

const OpportunityFilters: React.FC<Props> = ({ onSearch, onReset, initialValues }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const agendaFilterOptions = useAppSelector(agendaFilterOptionsSelector);
  const filtriCl = useAppSelector(filtriClSelector);
  const isAdmin = AuthService.isSuperAdmin();

  const businessUnits = useMemo(
    () => (isAdmin ? filtriCl?.businessUnit : filtriCl?.onlyChildBusinessUnit) || [],
    [isAdmin, filtriCl?.businessUnit, filtriCl?.onlyChildBusinessUnit],
  );

  useEffect(() => {
    if (!agendaFilterOptions?.accounts?.length) dispatch(getAgendaFilterOptions());
    if (!filtriCl?.businessUnit?.length && !filtriCl?.onlyChildBusinessUnit?.length) {
      dispatch(getClFilters());
    }
  }, []);

  const handleFinish = (values: any) => {
    const filters: Partial<RptOpportunitySearchDto> = {};
    if (values.denominazioneOpportunity?.trim()) filters.denominazioneOpportunity = values.denominazioneOpportunity.trim();
    if (values.fkCliente) filters.fkCliente = values.fkCliente;
    if (values.fkStrutturaBusiness?.length) filters.fkStrutturaBusiness = values.fkStrutturaBusiness;
    if (values.percProbMin != null) filters.percProbMin = values.percProbMin;
    if (values.percProbMax != null) filters.percProbMax = values.percProbMax;
    if (values.dataPotenzialeAvvioDa) filters.dataPotenzialeAvvioDa = new Date(values.dataPotenzialeAvvioDa.format('YYYY-MM-DD'));
    if (values.dataPotenzialeAvvioA) filters.dataPotenzialeAvvioA = new Date(values.dataPotenzialeAvvioA.format('YYYY-MM-DD'));
    if (values.stati?.length) filters.stati = values.stati;
    if (values.lastModified) filters.lastModified = new Date(values.lastModified.format('YYYY-MM-DD'));
    onSearch(filters);
  };

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  const formInitialValues = useMemo(() => ({
    stati: initialValues?.stati,
    lastModified: initialValues?.lastModified ? dayjs(initialValues.lastModified) : undefined,
  }), [initialValues]);

  // Sincronizza i campi quando i default arrivano dopo il mount (es. statiOpportunity ancora in fetch)
  useEffect(() => {
    form.setFieldsValue(formInitialValues);
  }, [formInitialValues]);

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical" initialValues={formInitialValues}>
      <Row className="row-cols-lg-5">
        <Col xs={12} sm={12} md={6}>
          <Form.Item name="denominazioneOpportunity" label={t('Nome opportunity')} className="mb-2">
            <Input allowClear placeholder={t('Cerca per nome')} />
          </Form.Item>
        </Col>

        <Col xs={12} sm={12} md={6}>
          <Form.Item name="fkCliente" label={t('Cliente')} className="mb-2">
            <Select
              showSearch
              allowClear
              placeholder={t('Seleziona cliente')}
              optionFilterProp="children"
              filterOption={filterOption}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {agendaFilterOptions?.accounts?.map((el) => (
                <Select.Option key={el.idAccount} value={el.idAccount}>
                  {el.nome}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={12} sm={12} md={6}>
          <Form.Item name="fkStrutturaBusiness" label={t('Struttura di business')} className="mb-2">
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder={t('Seleziona struttura')}
              optionFilterProp="children"
              filterOption={filterOption}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {businessUnits.map((el) => (
                <Select.Option key={el.id} value={el.id}>
                  {el.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={12} sm={12} md={6}>
          <Form.Item name="dataPotenzialeAvvioDa" label={t('Data potenziale avvio da')} className="mb-2">
            <DatePicker
              className="w-100"
              format="DD/MM/YYYY"
              allowClear
              placeholder={t('Seleziona data')}
              suffixIcon={<i className="far fa-calendar text-blue-links" />}
            />
          </Form.Item>
        </Col>

        <Col xs={12} sm={12} md={6}>
          <Form.Item
            name="dataPotenzialeAvvioA"
            label={t('Data potenziale avvio a')}
            className="mb-2"
            dependencies={['dataPotenzialeAvvioDa']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const da = getFieldValue('dataPotenzialeAvvioDa');
                  if (!value || !da || !dayjs(value).isBefore(dayjs(da), 'day')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t('La data "a" deve essere successiva alla data "da"'))
                  );
                },
              }),
            ]}
          >
            <DatePicker
              className="w-100"
              format="DD/MM/YYYY"
              allowClear
              placeholder={t('Seleziona data')}
              suffixIcon={<i className="far fa-calendar text-blue-links" />}
            />
          </Form.Item>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Item name="percProbMin" label={t('Prob. min (%)')} className="mb-2">
            <InputNumber className="w-100" min={0} max={100} placeholder="0" />
          </Form.Item>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Item name="percProbMax" label={t('Prob. max (%)')} className="mb-2">
            <InputNumber className="w-100" min={0} max={100} placeholder="100" />
          </Form.Item>
        </Col>

        <Col xs={12} sm={12} md={6}>
          <Form.Item name="stati" label={t('Stato')} className="mb-2">
            <Select
              mode="multiple"
              allowClear
              placeholder={t('Seleziona stato')}
              optionFilterProp="children"
              filterOption={filterOption}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {(agendaFilterOptions?.statiOpportunity || []).map((s) => (
                <Select.Option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={12} sm={12} md={6}>
          <Form.Item name="lastModified" label={t('Data ultima modifica')} className="mb-2">
            <DatePicker
              className="w-100"
              format="DD/MM/YYYY"
              allowClear
              placeholder={t('Seleziona data')}
              suffixIcon={<i className="far fa-calendar text-blue-links" />}
            />
          </Form.Item>
        </Col>
      </Row>

      <div className="d-flex mb-3">
        <div className="d-flex ms-auto">
          <Button className="btn text-danger trash-btn" type="button" variant="icon" onClick={handleReset}>
            <i className="fa-regular fa-trash-can me-2" />
            {t('project-list.delete-filters')}
          </Button>
          <LinksButton type="submit" className="btn-primary btn-links btn-sm px-4">
            <i className="fa-solid fa-magnifying-glass me-2" />
            {t('project-list.search')}
          </LinksButton>
        </div>
      </div>
    </Form>
  );
};

export default OpportunityFilters;
